/**
 * Componente que representa o cabeçalho do OrderDetails
 * 
 * Decisões de UI/UX:
 * - Layout responsivo otimizado para 285px
 * - Destaque para informações críticas (pedido, material, qualidade)
 * - Uso eficiente do espaço horizontal
 * - Informações agrupadas logicamente
 */

import React, { useState } from 'react';
import { XIcon as X } from '../../icons';
import { Button } from '../../ui';
// Ícones simples inline para evitar dependência externa
const Clock = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
);
const Hash = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg>
);
const Info = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
);
const HelpCircle = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 1 1 5.83 1c0 2-3 2-3 4"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
);
import { getMaterialColorById } from '../types/colors';
import { resolveQuality } from '../../../theme/resolvers';
import { contractionOptions, deliveryOptions, CONTRACTION_VARIABLE } from '../types/objects';

interface HeaderSectionProps {
  onClose: () => void;
  pedidoId: number;
  itemId: number;
  nomeFantasia: string;
  material: string;
  operationalMaterial?: string | null;
  quality: string;
  materialId: number;
  contraction: string;
  delivery: string;
  hasMultipleContractions: boolean;
  onContractionChange: (value: string) => void;
  onDeliveryChange: (value: string) => void;
  role?: string; // Adicionar role para verificar se é produção
  productProId?: number; // pro_id do produto para regras de UI (ex.: esconder contração)
  productCategId?: number; // pro_categ_id do produto para regras de UI (ex.: esconder contração para sublimação)
  cliente?: {
    cli_id: number;
    nome: string;
    razao_social: string;
    nome_fantasia: string;
  };
  isMonitorando?: boolean;
  onToggleMonitorStatus?: () => void;
}

export const HeaderSection: React.FC<HeaderSectionProps> = ({
  onClose,
  pedidoId,
  itemId,
  nomeFantasia,
  material,
  operationalMaterial,
  quality,
  materialId,
  contraction,
  delivery,
  hasMultipleContractions,
  onContractionChange,
  onDeliveryChange,
  role,
  productProId,
  productCategId,
  cliente,
  isMonitorando,
  onToggleMonitorStatus
}) => {
  // Estado para controlar a exibição do tooltip de informações do cliente
  const [showClientTooltip, setShowClientTooltip] = useState(false);
  const [showIdTooltip, setShowIdTooltip] = useState(false);
  
  // No fluxo atual, a contração por objeto pode ser editada em produção.
  // O seletor global do header permanece bloqueado para manter o fluxo simplificado.
  const isProducao = role === 'producao';
  
  // ✅ Ocultar "Contração" para:
  // - Categoria 3 (pro_categ_id=3) - Sublimação
  // - Categoria 11 (pro_categ_id=11) - Sublimação
  const hiddenContractionCategIds: number[] = [3, 11]; // Sublimação
  
  const hideByCategId = productCategId && hiddenContractionCategIds.includes(Number(productCategId));
  const showContraction = !hideByCategId;

  return (
    <div className="bg-navy-900 border-b border-navy-700 p-2 min-w-0">
      {/* Cabeçalho otimizado */}
      <div className="flex items-center justify-between mb-1.5 min-w-0">
        <div className="flex items-center gap-1 min-w-0 flex-1">
          <Hash className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div className="flex items-center min-w-0">
            <span className="text-base font-bold text-white">{pedidoId}</span>
            <span className="text-gray-600 mx-1 flex-shrink-0">/</span>
            <span className="text-base font-semibold text-gray-300">{itemId}</span>
          </div>
          <div className="relative flex-shrink-0">
            <HelpCircle 
              className="w-3.5 h-3.5 text-gray-400 ml-0.5 hover:text-blue-400 transition-colors cursor-help" 
              onMouseEnter={() => setShowIdTooltip(true)}
              onMouseLeave={() => setShowIdTooltip(false)}
            />
            {showIdTooltip && (
              <div className="absolute top-full left-0 mt-1 px-2 py-1.5 bg-navy-700 text-white text-xs rounded whitespace-nowrap z-10">
                <p><span className="text-blue-400 font-bold">#{pedidoId}</span>: Número do Pedido</p>
                <p><span className="text-blue-400 font-bold">#{itemId}</span>: Número do Item</p>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Indicador mínimo de monitoramento */}
          <button
            onClick={onToggleMonitorStatus}
            className={`w-2.5 h-2.5 rounded-full ${isMonitorando ? 'bg-green-400' : 'bg-navy-700'} transition-colors`}
            title={isMonitorando ? 'Monitoramento ativo' : 'Monitoramento inativo'}
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="p-1 hover:bg-navy-800 rounded-lg transition-colors flex-shrink-0"
            icon={X}
          />
        </div>
      </div>

      {/* Informações da empresa - Destaque */}
      <div className="mb-2 flex items-center min-w-0">
        <span className="text-base text-white font-bold truncate flex-1 min-w-0" title={nomeFantasia}>
          {nomeFantasia}
        </span>
        <div className="relative flex-shrink-0">
          <Info 
            className="w-3.5 h-3.5 text-gray-400 ml-1 hover:text-blue-400 transition-colors cursor-help" 
            onMouseEnter={() => setShowClientTooltip(true)}
            onMouseLeave={() => setShowClientTooltip(false)}
            onClick={() => setShowClientTooltip(!showClientTooltip)} // Toggle ao clicar também
          />
          {showClientTooltip && (
            <div className="absolute top-full right-0 mt-1 px-2 py-1.5 bg-navy-700 text-white text-xs rounded min-w-[160px] z-10">
              <p className="mb-1 font-semibold">Dados do Cliente:</p>
              <p><span className="text-blue-400">ID:</span> {cliente?.cli_id || '-'}</p>
              <p><span className="text-blue-400">Nome:</span> {cliente?.nome || '-'}</p>
              <p><span className="text-blue-400">Razão Social:</span> {cliente?.razao_social || '-'}</p>
              <p><span className="text-blue-400">Nome Fantasia:</span> {cliente?.nome_fantasia || nomeFantasia}</p>
            </div>
          )}
        </div>
      </div>

      {/* Material e Qualidade em destaque (empilhados) */}
      <div className="grid grid-cols-1 gap-1.5 mb-2 min-w-0">
        <div className="min-w-0">
          <span 
            className={`inline-flex px-2 py-0.5 rounded-md text-xs font-semibold tracking-wide ${getMaterialColorById(materialId)} w-full justify-center truncate`}
            title={`Produto do pedido: ${material}`}
          >
            {material}
          </span>
        </div>
        {operationalMaterial && operationalMaterial !== material && (
          <div className="min-w-0">
            <span
              className="inline-flex px-2 py-0.5 rounded-md text-[11px] font-medium tracking-wide bg-navy-700 text-cyan-200 border border-cyan-400/30 w-full justify-center truncate"
              title={`Item interno/Corel: ${operationalMaterial}`}
            >
              {operationalMaterial}
            </span>
          </div>
        )}
        <div className="min-w-0">
          <span 
            className={`inline-flex px-2 py-0.5 rounded-md text-xs font-semibold tracking-wide text-white ${resolveQuality(quality)} w-full justify-center truncate`}
            title={`Qualidade: ${quality}`}
          >
            {quality}
          </span>
        </div>
      </div>

      {/* Contração e Entrega */}
      <div className="flex gap-2 min-w-0">
        {showContraction && (
          <div className="flex-1 min-w-0">
            <label className="block text-[10px] text-gray-400 mb-0.5">
              Contração
              {isProducao && <span className="text-orange-400 ml-1">(Bloqueado)</span>}
            </label>
            <div className="relative">
              <select
                value={hasMultipleContractions ? CONTRACTION_VARIABLE : contraction}
                onChange={(e) => onContractionChange(e.target.value)}
                disabled={isProducao}
                className={`w-full bg-navy-800 border border-navy-700 rounded-lg px-2 py-1 text-xs text-white appearance-none min-w-0 ${
                  isProducao ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                title={isProducao ? "Campo bloqueado para usuários de produção" : "Contração aplicada ao material"}
              >
                {contractionOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
        )}

        <div className={`${showContraction ? 'flex-1' : 'w-full'} min-w-0`}>
          <label className="flex items-center text-[10px] text-gray-400 mb-0.5">
            <Clock className="w-3 h-3 mr-1 flex-shrink-0" />
            Entrega
            {isProducao && <span className="text-orange-400 ml-1">(Bloqueado)</span>}
          </label>
          <div className="relative">
            <select
              value={delivery}
              onChange={(e) => onDeliveryChange(e.target.value)}
              disabled={isProducao}
              className={`w-full bg-navy-800 border border-navy-700 rounded-lg px-2 py-1 text-xs text-white appearance-none min-w-0 ${
                isProducao ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              title={isProducao ? "Campo bloqueado para usuários de produção" : "Prazo de entrega"}
            >
              {deliveryOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
              <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 